import React, { useEffect, useMemo, useState } from "react";
import { db, auth } from "../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  orderBy,
} from "firebase/firestore";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Paper,
  Stack,
  Chip,
} from "@mui/material";
import moment from "moment";

function Userlogs() {
  const [propertyId, setPropertyId] = useState(null);
  const [logs, setLogs] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* ================= GET PROPERTY ================= */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return;

      const roleSnap = await getDoc(doc(db, "authroles", user.uid));
      if (!roleSnap.exists()) return;

      setPropertyId(roleSnap.data().propertyId);
    });

    return () => unsub();
  }, []);

  /* ================= FETCH USERS ================= */
  useEffect(() => {
    if (!propertyId) return;

    const fetchUsers = async () => {
      const q = query(
        collection(db, "authroles"),
        where("propertyId", "==", propertyId)
      );

      const snap = await getDocs(q);

      const map = {};
      snap.forEach((doc) => {
        map[doc.id] = doc.data();
      });

      setUsersMap(map);
    };

    fetchUsers();
  }, [propertyId]);

  /* ================= FETCH RESTO LOGS ================= */
  useEffect(() => {
    if (!propertyId) return;

    const q = query(
      collection(db, "restologs"),
      where("propertyId", "==", propertyId),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setLogs(data);
    });

    return () => unsub();
  }, [propertyId]);

  /* ================= FILTER ================= */
  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      const user = usersMap[log.userid];

      const searchText = `
        ${log.action || ""}
        ${log.module || ""}
        ${log.description || ""}
        ${log.orderid || ""}
        ${log.itemid || ""}
        ${user?.name || ""}
        ${user?.email || ""}
      `
        .toLowerCase()
        .trim();

      return searchText.includes(search.toLowerCase());
    });
  }, [logs, usersMap, search]);

  /* ================= PAGINATION ================= */
  const paginatedLogs = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredLogs.slice(start, start + rowsPerPage);
  }, [filteredLogs, page, rowsPerPage]);

  return (
    <Box className=" p-3">
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" mb={2}>
            <Typography variant="h6">User Activity Logs</Typography>
          </Stack>

          <TextField
            fullWidth
            size="small"
            placeholder="Search action / module / description / user..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ mb: 2 }}
          />

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Module</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedLogs.length > 0 ? (
                  paginatedLogs.map((log, index) => {
                    const user = usersMap[log.userid];

                    return (
                      <TableRow key={log.id}>
                        <TableCell>
                          {page * rowsPerPage + index + 1}
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={log.action}
                            size="small"
                            color={
                              log.action === "DELETE"
                                ? "error"
                                : log.action === "UPDATE"
                                ? "warning"
                                : log.action === "ADD"
                                ? "success"
                                : "primary"
                            }
                          />
                        </TableCell>

                        <TableCell>{log.module}</TableCell>

                        <TableCell>{log.description}</TableCell>

                        <TableCell>
                          {user?.name || log.useremail || "Unknown"}
                        </TableCell>

                        <TableCell>
                          {log.createdAt?.toDate
                            ? moment(log.createdAt.toDate()).format(
                                "DD-MM-YYYY HH:mm"
                              )
                            : ""}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No logs found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={filteredLogs.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}

export default Userlogs;